import React, { useState } from "react"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }

// Метрики «в час» при всех филиалах бэкенд делит на число филиалов с продажами
const PER_BRANCH_KEYS = ["cph", "revenue_per_hour"]

const hourLabel = (hour) => `${String(hour).padStart(2, "0")}:00`
const startHourOptions = Array.from({ length: 24 }, (_, hour) => ({ id: hour, name: hourLabel(hour) }))
const endHourOptions = Array.from({ length: 24 }, (_, index) => ({ id: index + 1, name: hourLabel(index + 1) }))

// Окно часов может переходить через полночь: «с 18:00 до 03:00»
function hourWindowHint(startHour, endHour) {
	const start = Number(startHour)
	const end = Number(endHour)
	if (start === end % 24) return "круглые сутки"
	if (end % 24 !== 0 && start > end) return "окно через полночь"
	return ""
}

// Рост по знаку growth_percent; для всех метрик экрана рост — хорошо
function growthMeta(metric) {
	const growth = metric.growth_percent
	if (growth === null || growth === undefined) {
		const isNew = metric.value !== null && Number(metric.value) > 0
		return { color: "var(--dark-text-muted, #8791a1)", background: "var(--dark-surface-2, #f1f4f8)", icon: null, text: isNew ? "новое" : "—" }
	}
	const number = Number(growth)
	const text = `${number > 0 ? "+" : ""}${number.toFixed(1).replace(".0", "").replace(".", ",")}%`
	if (number > 0) return { color: "var(--dark-fg-success, #1f9d57)", background: "var(--dark-tint-success, #e7f8ef)", icon: "uil-arrow-up", text }
	if (number < 0) return { color: "var(--dark-fg-danger, #d23434)", background: "var(--dark-tint-danger, #fdecec)", icon: "uil-arrow-down", text }
	return { color: "var(--dark-text-muted, #8791a1)", background: "var(--dark-surface-2, #f1f4f8)", icon: null, text }
}

function KpiCard({ metric, note }) {
	const meta = growthMeta(metric)
	return (
		<div className="col-xl-4 col-md-6 mb-3">
			<div className="card h-100" style={cardStyle}>
				<div className="card-body p-3">
					<div className="d-flex justify-content-between align-items-start gap-3">
						<div>
							<div className="d-flex align-items-center gap-2 mb-2" style={{ color: "var(--dark-text, #4b5563)", fontSize: "13px", fontWeight: 700 }}>
								<i className={`uil ${metric.icon || "uil-chart-line"}`} style={{ color: "var(--bs-primary)" }}></i>
								{metric.title}
							</div>
							<h3 className="mb-1" style={{ color: "var(--dark-text-strong, #111827)", fontSize: "26px", fontWeight: 900, letterSpacing: "-0.5px" }}>
								{metric.value === null ? "—" : metric.value_short}
							</h3>
							<div style={mutedStyle}>{metric.description}</div>
							{note && <div style={{ ...mutedStyle, marginTop: "2px" }}>{note}</div>}
						</div>
						<span
							style={{
								background: meta.background,
								color: meta.color,
								borderRadius: "999px",
								padding: "4px 10px",
								fontSize: "12px",
								fontWeight: 800,
								whiteSpace: "nowrap",
							}}
							title={metric.previous_value !== null && metric.previous_value !== undefined ? `Было: ${formatMoney(metric.previous_value)}` : undefined}
						>
							{meta.icon && <i className={`uil ${meta.icon}`}></i>}
							{meta.text}
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

function MissingMetrics({ items = [] }) {
	if (!items.length) return null
	return (
		<div className="card mb-3" style={{ ...cardStyle, borderColor: "var(--dark-tint-border-warning, #f3dfba)", background: "var(--dark-tint-warning, #fffaf0)" }}>
			<div className="card-body">
				<div className="d-flex align-items-center gap-2 mb-3" style={{ color: "var(--dark-fg-warning, #b7791f)", fontWeight: 800 }}>
					<i className="uil uil-exclamation-circle"></i>
					Метрики, требующие дополнительных данных
				</div>
				<div className="row g-3">
					{items.map(item => (
						<div className="col-xl-3 col-md-6" key={item.key}>
							<div className="d-flex align-items-start gap-2">
								<i className={`uil ${item.icon}`} style={{ color: "var(--dark-text-muted, #64748b)", marginTop: "2px" }}></i>
								<div>
									<div style={{ fontWeight: 800, color: "var(--dark-text, #4b5563)", fontSize: "13px" }}>{item.title}</div>
									<div style={mutedStyle}>{item.description}</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function PopMetrics() {
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
		start_hour: 9,
		end_hour: 24,
		sla_minutes: 5,
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/pop-metrics", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		if (!data?.metrics?.length) return
		const rows = data.metrics.map(item => {
			const growth = growthMeta(item).text
			return `<tr><td>${item.title}</td><td>${item.value === null ? "—" : item.value_short}</td><td>${growth}</td></tr>`
		}).join("")
		const html = `<table border="1"><thead><tr><th>Метрика</th><th>Значение</th><th>Рост</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `pop-metrics-${data.start_date || applied.start_date}_${data.end_date || applied.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const settings = data?.settings
	const totals = data?.totals || {}
	const allBranches = data ? data.pos_id === null || data.pos_id === undefined || data.pos_id === -1 : applied.pos_id === -1
	const perBranchNote = allBranches && data ? `На одну точку, филиалов с продажами: ${formatMoney(data.selling_branch_count || 0, 0)}` : ""
	const draftHint = hourWindowHint(draft.start_hour, draft.end_hour)

	let subtitle = "Ключевые операционные метрики периода к предыдущему периоду"
	if (settings) {
		const endHour = Number(settings.end_hour) === 0 ? 24 : settings.end_hour
		const hint = hourWindowHint(settings.start_hour, endHour)
		subtitle = `Часы ${hourLabel(settings.start_hour)}–${hourLabel(endHour)}${hint ? ` (${hint})` : ""} · SLA ${settings.sla_minutes} мин`
	}

	return (
		<div className="pb-3">
			<AnalyticsFilterBar
				title="PoP-метрики"
				icon="uil-chart-growth"
				subtitle={subtitle}
				filters={filters}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Часы с">
					<FilterSelect options={startHourOptions} value={Number(draft.start_hour)} onChange={(id) => patch({ start_hour: id })} />
				</FilterField>
				<FilterField label="Часы до">
					<FilterSelect options={endHourOptions} value={Number(draft.end_hour)} onChange={(id) => patch({ end_hour: id })} />
					{draftHint && <div style={{ ...mutedStyle, marginTop: "2px" }}>{draftHint}</div>}
				</FilterField>
				<FilterField label="SLA, мин">
					<input
						className="form-control"
						type="number"
						min="1"
						max="240"
						style={{ borderRadius: "10px", height: "38px" }}
						value={draft.sla_minutes}
						onChange={(event) => patch({ sla_minutes: event.target.value === "" ? "" : Number(event.target.value) })}
					/>
				</FilterField>
			</AnalyticsFilterBar>

			<div className="card mb-3" style={{ ...cardStyle, background: "var(--dark-surface-2, #fbfcfe)" }}>
				<div className="card-body py-3 d-flex flex-wrap gap-3">
					<span>Всего чеков: <b>{formatMoney(totals.cheques || 0, 0)}</b></span>
					<span>Выручка: <b>{formatMoney(totals.revenue || 0)}</b></span>
					{Number(totals.returns) > 0 && <span style={mutedStyle}>за вычетом возвратов {formatMoney(totals.returns)}</span>}
					{perBranchNote && <span style={mutedStyle}>CPH и выручка в час — {perBranchNote.toLowerCase()}</span>}
				</div>
			</div>

			<div className="row">
				{(data?.metrics || []).map(metric => (
					<KpiCard metric={metric} note={PER_BRANCH_KEYS.includes(metric.key) ? perBranchNote : ""} key={metric.key} />
				))}
			</div>

			{data?.compare_start_date && (
				<div className="mb-3" style={mutedStyle}>
					Рост — к периоду {formatPeriodLabel(data.compare_start_date, data.compare_end_date)}
				</div>
			)}

			<MissingMetrics items={data?.unavailable_metrics || []} />
		</div>
	)
}

export default PopMetrics
